import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon           from '@mui/icons-material/Close';
import ArrowForwardIcon    from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import OpenInNewIcon       from '@mui/icons-material/OpenInNew';
import { useSite }         from '../context/SiteContext';
import useAuthGuard        from '../hooks/useAuthGuard';
import usePageReady        from '../hooks/usePageReady';

// ─── Canvas constants — must match the builder ────────────────────────────────
const CANVAS_W = 1440;
const CANVAS_H = 810;
const ASPECT   = CANVAS_H / CANVAS_W; // 0.5625 (16:9)

// ─── localStorage helpers ─────────────────────────────────────────────────────
const INTRO_KEY    = (id) => `govirtual_booth_intro_${id}`;
const hasSeenIntro  = (id) => { try { return !!localStorage.getItem(INTRO_KEY(id)); } catch { return false; } };
const markIntroSeen = (id) => { try { localStorage.setItem(INTRO_KEY(id), '1');     } catch {} };

// ─── Geometry helpers — same as LobbyPage ────────────────────────────────────
// Exhibitor hotspot points are stored as percentages (0–100).
// Convert to pixel coords for viewBox="0 0 1440 810".
// pathD from the pen tool is already in pixel space — no scaling needed.
const toX = (pct) => (pct / 100) * CANVAS_W;
const toY = (pct) => (pct / 100) * CANVAS_H;

function toGeom(h) {
  const { shape = 'polygon', points = [], meta = {} } = h;

  if (shape === 'rect') {
    const [tl] = points;
    if (!tl) return null;
    return { kind: 'rect', x: toX(tl.x), y: toY(tl.y), w: toX(meta.width || 0), h: toY(meta.height || 0) };
  }

  if (shape === 'circle') {
    const [c] = points;
    if (!c) return null;
    return { kind: 'circle', cx: toX(c.x), cy: toY(c.y), r: toX(meta.radius || 0) };
  }

  // Bezier — check before points.length guard; pathD is already in pixel coords
  if (meta?.isBezier && meta?.pathD) {
    return { kind: 'path', d: meta.pathD };
  }

  if (!points.length) return null;
  return { kind: 'polygon', pts: points.map(p => `${toX(p.x)},${toY(p.y)}`).join(' ') };
}

function toCentroid(h) {
  const { shape = 'polygon', points = [], meta = {} } = h;
  if (shape === 'rect') {
    const [tl] = points;
    if (!tl) return null;
    return { cx: toX(tl.x) + toX(meta.width || 0) / 2, cy: toY(tl.y) + toY(meta.height || 0) / 2 };
  }
  if (shape === 'circle') {
    const [c] = points;
    return c ? { cx: toX(c.x), cy: toY(c.y) } : null;
  }
  // Prefer bezierPoints for centroid when available
  const pts = (meta?.isBezier && meta?.bezierPoints?.length ? meta.bezierPoints : points);
  if (!pts.length) return null;
  return {
    cx: pts.reduce((s, p) => s + toX(p.x), 0) / pts.length,
    cy: pts.reduce((s, p) => s + toY(p.y), 0) / pts.length,
  };
}

// ─── Individual hotspot shape ─────────────────────────────────────────────────
function HotspotShape({ geom, isHov, color, id }) {
  if (!geom) return null;
  const fill   = isHov ? `${color}30` : 'transparent';
  const stroke = isHov ? color        : 'transparent';
  const sw     = isHov ? 3            : 0;
  const filter = isHov ? `url(#glow-${id})` : undefined;
  const style  = { transition: 'fill 0.2s, stroke 0.2s' };

  if (geom.kind === 'rect')
    return <rect x={geom.x} y={geom.y} width={geom.w} height={geom.h}
      fill={fill} stroke={stroke} strokeWidth={sw} filter={filter} style={style} />;
  if (geom.kind === 'circle')
    return <circle cx={geom.cx} cy={geom.cy} r={geom.r}
      fill={fill} stroke={stroke} strokeWidth={sw} filter={filter} style={style} />;
  if (geom.kind === 'path')
    return <path d={geom.d}
      fill={fill} stroke={stroke} strokeWidth={sw} filter={filter} style={style} />;
  return <polygon points={geom.pts}
    fill={fill} stroke={stroke} strokeWidth={sw} filter={filter} style={style} />;
}

// ─── Hotspot SVG overlay ──────────────────────────────────────────────────────
// viewBox="0 0 1440 810" matches the builder exactly.
const HotspotOverlay = ({ hotspots = [], primary, onHotspotClick }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <svg
      viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        overflow: 'visible',
        zIndex: 2,
        pointerEvents: 'all',
      }}
    >
      <defs>
        {hotspots.filter(h => h.isActive).map(h => {
          const color   = h.glowColor || primary;
          const opacity = h.glowOpacity ?? 0.8;
          return (
            <filter key={h.id} id={`glow-${h.id}`} x="-25%" y="-25%" width="150%" height="150%">
              <feGaussianBlur stdDeviation="12" result="blur" />
              <feFlood floodColor={color} floodOpacity={opacity} result="color" />
              <feComposite in="color" in2="blur" operator="in" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          );
        })}
      </defs>

      {hotspots.filter(h => h.isActive).map(h => {
        const isHov    = hovered === h.id;
        const color    = h.glowColor || primary;
        const geom     = toGeom(h);
        const centroid = toCentroid(h);
        if (!geom) return null;

        const label = h.label || '';
        const pillW = label ? Math.max(label.length * 10.5 + 32, 70) : 0;
        const pillH = 34;

        return (
          <g
            key={h.id}
            onMouseEnter={() => setHovered(h.id)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onHotspotClick(h)}
            style={{ cursor: 'pointer' }}
          >
            <HotspotShape geom={geom} isHov={isHov} color={color} id={h.id} />

            {isHov && label && centroid && (
              <>
                <rect
                  x={centroid.cx - pillW / 2} y={centroid.cy - pillH / 2}
                  width={pillW} height={pillH} rx={pillH / 2}
                  fill="rgba(0,0,0,0.72)"
                />
                <text
                  x={centroid.cx} y={centroid.cy + 1}
                  textAnchor="middle" dominantBaseline="middle"
                  style={{
                    fill: '#fff', fontSize: 14, fontWeight: 700,
                    fontFamily: 'Nunito, Inter, sans-serif',
                    letterSpacing: '0.03em', pointerEvents: 'none',
                  }}
                >
                  {label}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ─── Intro Modal ──────────────────────────────────────────────────────────────
const IntroModal = ({ slides, primary, onClose }) => {
  const [slide, setSlide] = useState(0);
  const sorted  = [...slides].sort((a, b) => a.order - b.order);
  const current = sorted[slide];
  const isLast  = slide === sorted.length - 1;

  if (!current) { onClose(); return null; }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, bgcolor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box sx={{ width: '100%', height: '100%', maxWidth: 970, maxHeight: 550, bgcolor: '#fff', borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, boxShadow: '0 24px 80px rgba(0,0,0,0.3)', position: 'relative' }}>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, bgcolor: 'rgba(255,255,255,0.9)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: '0 0 44%', minHeight: { xs: 220, md: 0 }, bgcolor: '#0D1B2A', position: 'relative', overflow: 'hidden' }}>
          {current.mediaUrl && (current.mediaType === 'video'
            ? <Box component="video" src={current.mediaUrl} autoPlay muted loop playsInline sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <Box component="img" src={current.mediaUrl} alt={current.title} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
        </Box>
        <Box sx={{ flex: 1, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            {current.badge && (
              <Box sx={{ display: 'inline-flex', alignSelf: 'flex-start', bgcolor: `${primary}15`, color: primary, fontSize: 11, fontWeight: 700, letterSpacing: 1, px: 1.5, py: 0.4, borderRadius: 100, mb: 2, textTransform: 'uppercase' }}>
                {current.badge}
              </Box>
            )}
            <Typography variant="h3" sx={{ mb: 1.5, color: primary, lineHeight: 1.2 }}>{current.title}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.7 }}>{current.description}</Typography>
          </Box>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Button variant="contained" endIcon={<ArrowForwardIcon />}
              onClick={() => { if (current.ctaUrl) window.open(current.ctaUrl, '_blank'); if (isLast) onClose(); else setSlide(s => s + 1); }}
              sx={{ bgcolor: primary, borderRadius: 100, px: 3, fontWeight: 700, '&:hover': { bgcolor: primary, filter: 'brightness(0.9)' } }}>
              {current.ctaLabel || (isLast ? 'Explore' : 'Next')}
            </Button>
            {sorted.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                {sorted.map((_, i) => (
                  <Box key={i} onClick={() => setSlide(i)} sx={{ width: i === slide ? 24 : 8, height: 8, borderRadius: 100, bgcolor: i === slide ? primary : '#CBD5E1', cursor: 'pointer', transition: 'all 0.2s' }} />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Action Modal ─────────────────────────────────────────────────────────────
// Handles: image, video, pdf, iframe, game (legacy).
// 'link' is handled upstream in handleHotspotClick — never reaches here.
// Videos from action.type === 'video' always embed here, never open a new tab.
const ActionModal = ({ hotspot, primary, onClose }) => {
  const [galleryIdx, setGalleryIdx] = useState(0);
  if (!hotspot) return null;
  const { action, meta = {}, label } = hotspot;
  if (action.type === 'link') return null;

  // Resolve embed URL for video action (YouTube watch → embed, Vimeo → player)
  const resolveVideoUrl = (url = '') => {
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;
    const vi = url.match(/vimeo\.com\/(\d+)/);
    if (vi) return `https://player.vimeo.com/video/${vi[1]}?autoplay=1`;
    return url;
  };

  const isGallery = meta.galleryImages?.length > 0;

  return (
    <Box onClick={onClose} sx={{
      position: 'fixed', inset: 0, zIndex: 1500,
      bgcolor: 'rgba(0,0,0,0.82)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: { xs: 1, md: 3 },
    }}>
      <Box onClick={e => e.stopPropagation()} sx={{
        position: 'relative', width: '100%',
        maxWidth: action.type === 'image' || isGallery ? 960 : 1100,
        maxHeight: '92vh',
        bgcolor: '#0a0a0a', borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 40px 120px rgba(0,0,0,0.7)',
        display: 'flex', flexDirection: 'column',
      }}>
        <IconButton onClick={onClose} size="small" sx={{
          position: 'absolute', top: 10, right: 10, zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
        }}>
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* ── Gallery (meta.galleryImages) ── */}
        {isGallery && (() => {
          const imgs    = meta.galleryImages;
          const curr    = imgs[galleryIdx];
          const hasPrev = galleryIdx > 0;
          const hasNext = galleryIdx < imgs.length - 1;
          return (
            <>
              <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}>
                <Box component="img" src={curr.url} alt={curr.caption || `Slide ${galleryIdx + 1}`}
                  sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block', userSelect: 'none' }} />
                {hasPrev && (
                  <IconButton onClick={() => setGalleryIdx(i => i - 1)} sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                )}
                {hasNext && (
                  <IconButton onClick={() => setGalleryIdx(i => i + 1)} sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {(curr.caption || imgs.length > 1) && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  {curr.caption && <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>{curr.caption}</Typography>}
                  {imgs.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {imgs.map((_, i) => (
                        <Box key={i} onClick={() => setGalleryIdx(i)} sx={{ width: i === galleryIdx ? 20 : 7, height: 7, borderRadius: 100, bgcolor: i === galleryIdx ? '#fff' : 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s' }} />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          );
        })()}

        {/* ── Image lightbox ── */}
        {!isGallery && action.type === 'image' && (
          <>
            <Box component="img" src={action.imageUrl} alt={action.imageCaption || label}
              sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
            {action.imageCaption && (
              <Box sx={{ px: 3, py: 2, bgcolor: '#111' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>{action.imageCaption}</Typography>
              </Box>
            )}
          </>
        )}

        {/* ── Video embed — always in modal, never a new tab ── */}
        {!isGallery && action.type === 'video' && (
          <>
            {action.videoTitle && (
              <Box sx={{ px: 3, py: 2, bgcolor: '#111', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>{action.videoTitle}</Typography>
              </Box>
            )}
            <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
              <Box component="iframe"
                src={resolveVideoUrl(action.videoUrl)}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            </Box>
          </>
        )}

        {/* ── PDF viewer ── */}
        {!isGallery && action.type === 'pdf' && (
          <>
            {action.pdfTitle && (
              <Box sx={{ px: 3, py: 2, bgcolor: '#111', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>{action.pdfTitle}</Typography>
                <Button href={action.pdfUrl} target="_blank" rel="noopener noreferrer" size="small" variant="outlined"
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', fontSize: 12, '&:hover': { borderColor: '#fff' } }}>
                  Open in new tab
                </Button>
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: '70vh' }}>
              <Box component="iframe" src={`${action.pdfUrl}#toolbar=1`} title={action.pdfTitle}
                sx={{ width: '100%', height: '100%', border: 'none', display: 'block', minHeight: '70vh' }} />
            </Box>
          </>
        )}

        {/* ── iFrame / game (legacy) ── */}
        {!isGallery && (action.type === 'iframe' || action.type === 'game') && (
          <>
            {action.iframeTitle && (
              <Box sx={{ px: 3, py: 2, bgcolor: '#111', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>{action.iframeTitle}</Typography>
                {action.iframeSrc && (
                  <IconButton component="a" href={action.iframeSrc} target="_blank" rel="noopener noreferrer" size="small" sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: '70vh' }}>
              <Box component="iframe"
                src={action.iframeSrc}
                title={action.iframeTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; fullscreen"
                allowFullScreen
                sx={{ width: '100%', height: '100%', border: 'none', display: 'block', minHeight: '70vh' }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN EXHIBITOR BOOTH PAGE
// ═════════════════════════════════════════════════════════════════════════════
const ExhibitorBoothPage = ({ preloadedExhibitor }) => {
  const { event } = useSite();
  const navigate  = useNavigate();
  const exhibitor = preloadedExhibitor;
  const branding  = event?.branding || {};
  const primary   = exhibitor?.accentColor || branding.primaryColor || '#1565C0';

  const { authChecked } = useAuthGuard(event);
  usePageReady();

  const [showIntro,     setShowIntro]     = useState(false);
  const [activeHotspot, setActiveHotspot] = useState(null);

  useEffect(() => {
    if (!exhibitor) return;
    if (exhibitor.showIntro && exhibitor.introSlides?.length > 0 && !hasSeenIntro(exhibitor._id)) {
      setShowIntro(true);
    }
  }, [exhibitor]);

  const handleHotspotClick = useCallback((hotspot) => {
    const { action } = hotspot;
    if (!action) return;

    if (action.type === 'link' && action.linkUrl) {
      // Link — navigate, never a modal
      if ((action.linkTarget || '_self') === '_blank') {
        window.open(action.linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(action.linkUrl);
      }
      return;
    }

    // Everything else (image, video, pdf, iframe, game) → modal
    setActiveHotspot(hotspot);
  }, [navigate]);

  if (!authChecked) return null;

  if (!exhibitor) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0D1B2A' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Exhibitor not found</Typography>
      </Box>
    );
  }

  return (
    // Full-viewport dark bg — letterboxes on non-16:9 screens
    <Box sx={{
      width: '100vw', height: '100vh',
      position: 'relative', overflow: 'hidden',
      bgcolor: '#000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>

      {/*
        ─── 16:9 canvas box ────────────────────────────────────────────────────
        Mirrors the builder sizing exactly so hotspot percent-coords land
        precisely over the visible booth image.
      */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        maxWidth: `min(100vw, calc(100vh / ${ASPECT}))`,
        aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
        overflow: 'hidden',
      }}>

        {/* Background */}
        {exhibitor.boothBgVideo ? (
          <Box component="video" src={exhibitor.boothBgVideo} autoPlay muted loop playsInline
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : exhibitor.boothBgImage ? (
          <Box component="img" src={exhibitor.boothBgImage} alt={exhibitor.name}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          // Fallback solid colour from boothBgColor
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: exhibitor.boothBgColor || '#0D1B2A' }} />
        )}

        {/* Subtle vignette */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at 50% 60%, transparent 50%, rgba(0,0,0,0.28) 100%)',
        }} />

        {/* Hotspot SVG — viewBox="0 0 1440 810" matches the builder */}
        <HotspotOverlay
          hotspots={exhibitor.hotspots || []}
          primary={primary}
          onHotspotClick={handleHotspotClick}
        />
      </Box>

      {/* Intro modal */}
      {showIntro && exhibitor.introSlides?.length > 0 && (
        <IntroModal
          slides={exhibitor.introSlides}
          primary={primary}
          onClose={() => { markIntroSeen(exhibitor._id); setShowIntro(false); }}
        />
      )}

      {/* Action modal */}
      {activeHotspot && (
        <ActionModal
          hotspot={activeHotspot}
          primary={primary}
          onClose={() => setActiveHotspot(null)}
        />
      )}
    </Box>
  );
};

export default ExhibitorBoothPage;